<template>
  <div>
    <div id="printable-appointments">
      <template v-for="appointment in printableAppointments" :key="appointment.id">
        <printable-appointment-details
          :settings="settings"
          :appointment="appointment"
          :custom-form-data="appointment.customFormData"></printable-appointment-details>
      </template>
    </div>

    <v-card class="appointment-list">
      <v-card-title>
        <v-container fluid>
          <v-row align="center" v-if="customHeader">
            <slot
              name="table-header"
              :filters="filters"
              :rows="listableAppointments"
              :columns="{
                available: selectColumns,
                selected: selectedColumns,
                autoSelect: selectedColumnsAutoselect,
                update: event => (selectedColumns = event)
              }"
              :exporter="{
                exportExcel: exportToExcel,
                excelFileName: excelFileName,
                buildingExcelData: buildingExcelData,
                printTable: printTable,
                printTableAsIs: printTableAsIs
              }"></slot>
          </v-row>
          <v-row align="center" v-else class="mb-2 pl-3">
            <v-col cols="6">
              <text-field
                class="mb-5"
                v-model="filters.searchStr"
                append-icon="mdi-filter"
                label="Filter"
                hint="Search for appointments"
                persistent-hint></text-field>
            </v-col>
            <v-col cols="6">
              <generic-multi-select
                :items="selectColumns"
                class="mb-5"
                item-text="text"
                item-value="value"
                v-model="selectedColumns"
                append-icon="mdi-list"
                refKey="columnSelect"
                :auto-select="selectedColumnsAutoselect"
                :minimum-selections="1"
                :visible-selection-count="3"
                label="Table Columns"
                return-object
                as-chips
                hint="Select columns to show in the table"
                multiple
                persistent-hint></generic-multi-select>
            </v-col>
            <v-spacer></v-spacer>
            <primary-button
              v-if="showExportButton"
              data-testid="export-to-excel"
              class="mr-3"
              :loading="buildingExcelData"
              before-icon="microsoft-excel"
              @click="exportToExcel(exportableRows, excelFileName)">
              Export to Excel
            </primary-button>
            <primary-button before-icon="printer" @click="printTable" v-if="showPrintButton">
              Print Appointments
            </primary-button>
            <v-spacer></v-spacer>
            <span class="font-size-large text--black">
              {{ appointments.length > 0 ? `Total Appointments: ${appointments.length}` : '' }}
            </span>
          </v-row>
        </v-container>
      </v-card-title>
      <appointments-loader :is-loading="isLoading"></appointments-loader>
      <div>
        <!-- This is not the printable table; this is the one we show on the screen -->
        <v-data-table
          data-testid="appointments-list-data-table"
          :headers="columnsToShow"
          :items="listableAppointments"
          :loading="$data.$globalLoading"
          @update:sort-by="sortByVal => (sortBy = sortByVal)"
          ref="appointmentListTable"
          :search="regexSafeSearch"
          :custom-filter="searchRows"
          @current-items="setExportRows"
          id="appointments-table"
          item-key="id"
          :items-length="appointments.length"
          :expanded="expanded"
          :items-per-page-options="footerProps.itemsPerPageOptions"
          :items-per-page="options.itemsPerPage"
          class="clickable-rows print-hidden">
          <template
            v-for="header in columnsToShow.filter(
              column => !['actions', 'customFields'].includes(column.key)
            )"
            v-slot:[`item.${header.key}`]="{ value, item }">
            <template v-if="header.key === 'user.email'">
              <copy-content
                :key="header.key"
                :content="value"
                tooltip="Click to copy carrier email">
                <span v-html="highlight(value, regexSafeSearch)"></span>
              </copy-content>
            </template>

            <template v-else-if="header.key === 'status'">
              <div
                v-bind:key="header.key"
                :class="`bg-${item.status.toLowerCase()} tile font-size-x-small text-center`">
                <v-icon class="mr-1" v-if="novaCore.isRequested(item.status)">
                  mdi-clock-alert
                </v-icon>
                <strong
                  :class="{ 'black--text': novaCore.isRequested(item.status) }"
                  v-html="highlight(novaCore.breakWordsAtCaps(item.status), regexSafeSearch)" />
              </div>
            </template>

            <template v-else-if="header.key === 'startDate'">
              <span v-bind:key="header.key">
                {{
                  getTimeInWarehouseTimezone(
                    item,
                    novaCore.LuxonDateTimeFormats.LongDateShortMonth,
                    novaCore.LuxonDateTimeFormats.LongDateShortMonth,
                    $isMilitaryTimeEnabled(warehouse)
                  )
                }}
              </span>
            </template>

            <template v-else-if="header.key === 'startTime'">
              <span v-bind:key="header.key">
                {{
                  getTimeInWarehouseTimezone(
                    item,
                    `${novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM} - ${novaCore.LuxonDateTimeFormats.AbbreviatedNamedOffset}`,
                    `${novaCore.LuxonDateTimeFormats.Extended24HrTime} - ${novaCore.LuxonDateTimeFormats.AbbreviatedNamedOffset}`,
                    $isMilitaryTimeEnabled(warehouse)
                  )
                }}
              </span>
            </template>

            <template v-else-if="header.key === 'tags'">
              <div :key="header.key" @click="toggleRowExpansion(item)">
                <template v-if="item.tags?.length">
                  <v-chip
                    class="px-2 mr-1"
                    label
                    size="small"
                    :style="{ color: util.makeTagObject($org.customTags, item.tags[0]).textColor }"
                    :color="util.makeTagObject($org.customTags, item.tags[0]).color">
                    <span
                      v-html="
                        highlight(
                          util.makeTagObject($org.customTags, item.tags[0]).name,
                          regexSafeSearch
                        )
                      "></span>
                  </v-chip>
                  <span v-if="item.tags.length > 1">+ {{ item.tags.length - 1 }} more</span>
                </template>
              </div>
            </template>

            <template v-else>
              <span :key="header.key" v-html="highlight(value, regexSafeSearch)"></span>
            </template>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="row-actions">
              <icon-tooltip-button
                size="small"
                @click="handleEventClick(item)"
                tooltip="View Appointment"
                icon="eye" />
              <icon-tooltip-button
                size="large"
                @click="toggleRowExpansion(item)"
                tooltip="Show More"
                :icon="expanded.includes(item) ? 'chevron-up' : 'chevron-down'" />
            </div>
          </template>

          <template
            v-slot:expanded-row="{ columns: headers, item }"
            v-if="headers && headers.length">
            <td :colspan="headers.length" class="py-6 px-4 elevation-4 print-hidden">
              <div class="d-flex flex-column gap-6">
                <div class="d-flex flex-column gap-2">
                  <h3 class="typography-heading-md-bold">Tags</h3>

                  <div class="d-inline-block">
                    <v-chip
                      v-for="tag in item.tags"
                      :key="tag"
                      class="px-2 mr-1"
                      label
                      :style="{ color: util.makeTagObject($org.customTags, tag).textColor }"
                      :color="util.makeTagObject($org.customTags, tag).color">
                      <span
                        v-html="
                          highlight(util.makeTagObject($org.customTags, tag).name, regexSafeSearch)
                        "></span>
                    </v-chip>
                  </div>
                  <span v-if="!item.tags || !item.tags.length">
                    This appointment does not have tags
                  </span>
                </div>

                <v-divider></v-divider>

                <template v-if="filteredCustomFields(item)?.length">
                  <div class="d-flex flex-column gap-2">
                    <h3 class="typography-heading-md-bold">Custom Fields</h3>
                    <dl
                      v-for="customField in filteredCustomFields(item)"
                      :key="customField.name"
                      class="d-flex flex-row gap-2">
                      <dt class="typography-body-sm-bold">{{ customField.label }}:</dt>
                      <dd class="typography-body-sm">
                        <span
                          v-html="
                            highlight(getCustomFieldValue(customField, item), regexSafeSearch)
                          "></span>
                      </dd>
                    </dl>
                  </div>
                  <v-divider></v-divider>
                </template>

                <template v-if="loadTypeTriggers.length">
                  <div class="d-flex flex-column gap-2">
                    <h3 class="typography-heading-md-bold">Load Type Fields</h3>
                    <custom-forms-multiple-trigger-form
                      :warehouse-id="warehouse.id"
                      :is-editable="false"
                      :object-id="item.id"
                      :triggers="loadTypeTriggers"
                      :timezone="warehouse.timezone"
                      :military-time-enabled="$isMilitaryTimeEnabled(warehouse)" />
                  </div>

                  <v-divider></v-divider>
                </template>

                <div
                  v-for="trigger of $selectedWarehouseTriggers"
                  :key="trigger.id"
                  class="d-flex flex-column gap-2">
                  <h3 class="typography-heading-md-bold">
                    {{
                      trigger.flow?.name || novaCore.CustomFormsFeaturesData[trigger.feature].title
                    }}
                  </h3>
                  <custom-forms-data
                    :key="trigger.id"
                    :trigger="trigger"
                    :object-ids="{
                      appointmentId: item.id,
                      assetVisitId: item.assetVisit?.id
                    }"
                    :timezone="warehouse.timezone"
                    flat
                    :military-time-enabled="$isMilitaryTimeEnabled(warehouse)"></custom-forms-data>
                </div>
              </div>
            </td>
          </template>

          <template v-slot:no-data v-if="shouldShowNoSelectionOverlayMargin">
            <div style="height: 350px"></div>
          </template>
        </v-data-table>

        <!-- Printable Table this is a simplified version of the table above -->
        <v-data-table
          id="printable-appointments-table"
          :headers="selectedColumns"
          :items="listableAppointments"
          item-key="id"
          :items-length="listableAppointments.length"
          :items-per-page="listableAppointments.length"
          hide-default-footer
          disable-sort
          class="print-only">
          <template
            v-for="header in selectedColumns"
            v-slot:[`item.${header.key}`]="{ value, item }">
            <span :key="header.key" v-if="header.key === 'user.email'" v-html="value"></span>

            <template v-else-if="header.key === 'status'">
              <div :key="header.key" :class="`font-size-x-small text-center`">
                <strong v-html="novaCore.breakWordsAtCaps(item.status)" />
              </div>
            </template>

            <template v-else-if="header.key === 'startDate'">
              <span :key="header.key">
                {{
                  getTimeInWarehouseTimezone(
                    item,
                    novaCore.LuxonDateTimeFormats.LongDateShortMonth,
                    novaCore.LuxonDateTimeFormats.LongDateShortMonth,
                    $isMilitaryTimeEnabled(warehouse)
                  )
                }}
              </span>
            </template>

            <template v-else-if="header.key === 'startTime'">
              <span :key="header.key">
                {{
                  getTimeInWarehouseTimezone(
                    item,
                    `${novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM} - ${novaCore.LuxonDateTimeFormats.AbbreviatedNamedOffset}`,
                    `${novaCore.LuxonDateTimeFormats.Extended24HrTime} - ${novaCore.LuxonDateTimeFormats.AbbreviatedNamedOffset}`,
                    $isMilitaryTimeEnabled(warehouse)
                  )
                }}
              </span>
            </template>

            <template v-else-if="header.key === 'tags'">
              <div :key="header.key" class="font-size-x-small text-center">
                <template v-if="item.tags?.length">
                  <span v-html="util.makeTagObject($org.customTags, item.tags[0]).name"></span>
                  <span v-if="item.tags.length > 1">+ {{ item.tags.length - 1 }} more</span>
                </template>
              </div>
            </template>

            <template v-else>
              <span :key="header.key" v-html="value"></span>
            </template>
          </template>
        </v-data-table>
      </div>
    </v-card>

    <appointment-details-dialog
      v-if="selectedEvent"
      :external-activator="true"
      :show-dialog="showDetailsDialog"
      @close="handleDetailsDialogClose"
      :appointment="selectedEvent"></appointment-details-dialog>
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import dataListMixin from '@satellite/components/mixins/dataListMixin';
import exportAppointmentsMixin from '@satellite/components/mixins/exportAppointmentsMixin';
import { getNestedPropertyByDotNotation } from '@nova/core';
import { getTimeInWarehouseTimezone } from '@satellite/plugins/util';
import { escapeRegExp } from 'lodash';
import { isNumber } from 'class-validator';
import appointmentMixinBase from '@satellite/components/mixins/appointmentMixinBase.js';
import customFormsService from '@satellite/services/custom-forms-service';

export default {
  mixins: [dataListMixin, appointmentMixinBase, exportAppointmentsMixin],
  props: {
    overrides: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    appointments: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    customHeader: {
      type: Boolean,
      required: false,
      default: false
    },
    isRange: {
      type: Boolean,
      required: false,
      default: true
    },
    showExportButton: {
      type: Boolean,
      required: false,
      default: true
    },
    showPrintButton: {
      type: Boolean,
      required: false,
      default: true
    },
    buildHeadersFn: {
      type: Function,
      required: false,
      default: null
    },
    itemsPerPage: {
      type: Number,
      required: false,
      default: 15
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    formatAppointmentsFn: {
      type: Function,
      required: false,
      default: null
    },
    viewType: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      page: 1,
      sortBy: [
        {
          key: 'start',
          order: 'desc'
        }
      ],
      filters: {
        searchStr: null,
        dateRange: this.isRange ? [] : ''
      },
      showDetailsDialog: false,
      selectedEvent: null,
      expanded: [],
      options: {
        itemsPerPage: this.itemsPerPage
      },
      selectedColumns: [],
      columnsToShow: [],
      loadTypeTriggers: [],
      persistentColumns: [
        {
          title: 'Actions',
          key: 'actions',
          sortable: false,
          align: 'end'
        },
        {
          title: 'Custom Fields',
          key: 'customFields',
          searchable: true,
          align: ' d-none'
        }
      ],
      columnsStorageKey: 'appointmentList_selectedColumns',
      printableAppointments: [],
      /**
       * @type {('idle' | 'preparing' | 'printing')}
       */
      printStatus: 'idle',
      abortController: null
    };
  },
  computed: {
    settings() {
      return this.warehouse?.settings ?? {};
    },
    totalItems() {
      return this.listableAppointments.length;
    },
    excelFileName() {
      return `${this.$selectedWarehouse?.name || 'opendock'}_appointments.xlsx`;
    },
    listableAppointments() {
      let appointments = this.appointments;

      if (this.formatAppointmentsFn) {
        appointments = this.formatAppointmentsFn(appointments);
      }
      appointments = this.customSort(appointments);

      return appointments.filter(appointment => !this.novaCore.isReserve(appointment));
    },
    shouldShowNoSelectionOverlayMargin() {
      return !this.$selectedWarehouse?.id || !this.$selectedDocks?.length;
    },
    selectColumns() {
      const unselectableCols = ['customFields', 'actions'];
      return this.headers.filter(h => !unselectableCols.includes(h.key));
    },
    headers() {
      let headers;
      if (this.buildHeadersFn) {
        headers = this.buildHeadersFn();
      } else {
        headers = [
          {
            title: 'Conf #',
            key: 'confirmationNumber',
            searchable: true,
            idx: 0,
            width: 90
          },
          {
            title: 'Start Date',
            key: 'startDate',
            searchable: true,
            idx: 0
          },
          {
            title: 'Start Time',
            key: 'startTime',
            searchable: true,
            idx: 1
          },
          {
            title: 'Carrier Contact',
            key: 'user.email',
            searchable: true,
            idx: 2
          },
          {
            title: 'Carrier Company',
            key: 'user.company.name',
            searchable: true,
            sortable: true,
            idx: 3
          },
          {
            title: 'Location',
            key: 'dock.warehouse.name',
            searchable: true,
            align: ' d-none',
            idx: 4
          },
          {
            title: 'Dock',
            key: 'dock.name',
            searchable: true,
            idx: 5
          },
          {
            title: 'Load Type',
            key: 'loadType.name',
            searchable: true,
            idx: 6
          },
          {
            title: 'Direction',
            key: 'loadType.direction',
            searchable: true,
            idx: 7,
            align: 'd-none'
          },
          {
            title: 'Tags',
            key: 'tags',
            searchable: true,
            idx: 8
          },
          {
            title: this.$getSettingValue('referenceNumberDisplayName', this.warehouse?.settings),
            key: 'refNumber',
            searchable: true,
            idx: 9
          },
          {
            title: 'Status',
            key: 'status',
            searchable: true,
            idx: 10
          },
          {
            title: 'Appointment ID',
            key: 'id',
            searchable: true,
            idx: 11
          }
        ];
      }

      headers = [...headers, ...this.persistentColumns];

      return headers;
    },
    regexSafeSearch() {
      if (this.filters.searchStr) {
        return escapeRegExp(this.filters.searchStr);
      }

      return this.filters.searchStr;
    },
    warehouse() {
      // This is used by appointmentMixin
      return this.$selectedWarehouse;
    },
    storedSelectedColumns: {
      get() {
        const stored = localStorage.getItem(this.columnsStorageKey);
        if (!stored) {
          return [];
        }
        try {
          return JSON.parse(stored) || [];
        } catch {
          return [];
        }
      },
      set(value) {
        const count = value?.length ?? 0;
        if (count < 1) {
          return;
        }
        localStorage.setItem(this.columnsStorageKey, JSON.stringify(value));
      }
    },
    selectedColumnsAutoselect() {
      return this.storedSelectedColumns.length < 1;
    },
    isPrinting() {
      return ['printing'].includes(this.printStatus);
    },
    hasPrintingStarted() {
      return ['preparing', 'printing'].includes(this.printStatus);
    }
  },
  methods: {
    updatePage(page) {
      this.page = page;
    },
    async setLoadTypeTrigger(loadTypeId) {
      if (loadTypeId) {
        this.loadTypeTriggers = await customFormsService.getLoadTypeTriggers(loadTypeId);
      }
    },
    getTimeInWarehouseTimezone,
    highlight(value, query) {
      if (typeof value !== 'string') {
        return value;
      }
      const regex = new RegExp(query, 'ig');
      return value.replace(regex, match => `<span class='bg-primary'>${match}</span>`);
    },
    searchRows(value, search, item) {
      if (!value) {
        return false;
      }
      if (!search) {
        return true;
      }
      let hasMatch = false;
      let hasExpandedMatch = false;
      const isArr = Array.isArray(value);

      const s = search.toLowerCase();

      if (typeof value === 'string') {
        hasMatch = value.toLowerCase().search(s) !== -1;
      }

      if (isArr && !hasMatch) {
        const filtered = value.filter(i => {
          i = i && typeof i === 'object' ? Object.values(i).join(', ') : i;
          return typeof i === 'string' ? i.toLowerCase().search(s) !== -1 : false;
        });
        hasMatch = filtered.length > 0;
        hasExpandedMatch = hasMatch;
      }

      if (typeof value === 'object' && !isArr && !hasMatch) {
        hasMatch =
          Object.values(value).filter(i => {
            return i.toLowerCase().search(s) !== -1;
          }).length > 0;
        hasExpandedMatch = hasMatch;
      }

      if (hasExpandedMatch) {
        this.expanded.push(item.id);
      }

      return hasMatch;
    },
    toggleRowExpansion(item) {
      if (this.expanded.includes(item.id)) {
        const index = this.expanded.findIndex(i => i === item.id);
        this.expanded.splice(index, 1);
        this.loadTypeTriggers = [];
      } else {
        this.expanded.push(item.id);
        if (item.loadTypeId) {
          this.setLoadTypeTrigger(item.loadTypeId);
        }
      }
    },
    setExportRows() {
      if (this.$refs.appointmentListTable) {
        this.exportableRows = this.novaCore.deepClone(
          this.$refs.appointmentListTable?.$children[0].filteredItems
        );
      }
    },
    async handleEventClick(event) {
      this.$store.commit('Calendar/setSelectedEvent', event);
      if (this.novaCore.isReserve(event)) {
        this.showReserveDialog = this.$rolePermissions.canUpdateAppointment;
      } else {
        this.showDetailsDialog = true;
      }
      this.selectedEvent = event;
      await this.$router.replace({ query: { appointmentId: event.id } });
    },
    handleDetailsDialogClose() {
      this.$store.commit('Calendar/setSelectedEvent', null);
      this.showDetailsDialog = false;
    },
    shouldGetData(filters) {
      return !(this.isRange && filters.dateRange.length === 1);
    },
    filteredCustomFields(event) {
      if (event) {
        return this.novaCore.updateCustomFieldsFromWarehouse(
          event.customFields,
          event.dock.warehouse,
          false
        );
      }
      return [];
    },
    handleColumnSelect() {
      if (this.selectColumns?.length > 0) {
        this.storedSelectedColumns = this.selectedColumns;
      }
    },
    customSort(items) {
      const sortBy = this.sortBy[0];
      if (!sortBy || !items.length) {
        return items;
      }

      const objKey =
        sortBy.key === 'startDate'
          ? 'start'
          : sortBy.key === 'startTime'
          ? 'startClockSpan'
          : sortBy.key;

      const collator = new Intl.Collator('en', {
        numeric: true,
        sensitivity: 'base'
      });

      const sorted = items.sort((a, b) => {
        const primaryCompare = collator.compare(
          getNestedPropertyByDotNotation(a, objKey),
          getNestedPropertyByDotNotation(b, objKey)
        );

        // Sort the date when the time is sorted
        if (primaryCompare === 0 && objKey === 'startClockSpan') {
          return collator.compare(
            getNestedPropertyByDotNotation(a, 'start'),
            getNestedPropertyByDotNotation(b, 'start')
          );
        }

        return primaryCompare;
      });

      if (sortBy.order === 'desc') {
        sorted.reverse();
      }

      return sorted;
    },
    async printTable() {
      if (this.listableAppointments.length === 0) {
        this.notify('No appointments to print', 'info');
        return;
      }

      if (this.hasPrintingStarted) {
        this.notify('Printing is already in progress...', 'info', {
          duration: 2500
        });
        return;
      }

      this.notify('Preparing appointments for printing...', 'info', {
        duration: 2500
      });

      this.printStatus = 'preparing';

      this.printableAppointments = await this.enrichAppointmentsWithCustomFormData(
        this.listableAppointments
      );

      await this.$nextTick();

      this.handlePrint({
        sourceElementId: 'printable-appointments',
        containerToBeUsedAsPage: '.printable-window',
        printType: 'Detailed Appointments'
      });
    },
    updateSelectedColumns() {
      const cts = this.novaCore.deepClone(this.novaCore.sortBy(this.selectedColumns, 'idx'));
      this.columnsToShow = [...cts, ...this.persistentColumns];
    },
    async handleApptIdURLQuery() {
      const routeAppointmentId = this.$route.query.appointmentId;
      if (routeAppointmentId && routeAppointmentId !== !this.$selectedEvent?.id) {
        await this.fetchAndOpenAppt(routeAppointmentId);
      }
    },
    getCustomFieldValue(field, item) {
      if (field?.value || field.value === false || isNumber(field.value)) {
        return this.novaCore.getCustomFieldFormattedValue(field, {
          [this.novaCore.CustomFieldType.Document]: { generateLink: true },
          [this.novaCore.CustomFieldType.MultiDocument]: { generateLink: true },
          [this.novaCore.CustomFieldType.Timestamp]: {
            timezone: item?.dock?.warehouse?.timezone,
            formatAsMilitary: this.$isMilitaryTimeEnabled(item?.dock?.warehouse)
          }
        });
      }
    },
    printTableAsIs() {
      if (this.listableAppointments.length === 0) {
        this.notify('No appointments to print', 'info');
        return;
      }

      if (this.hasPrintingStarted) {
        this.notify('Printing is already in progress...', 'info', {
          duration: 2500
        });
        return;
      }

      this.printStatus = 'preparing';

      this.handlePrint({
        sourceElementId: 'printable-appointments-table',
        styles: `
          #print-content .v-data-table {
            width: 100% !important;
          }

          #print-content .v-table__wrapper {
            overflow: visible !important;
            height: auto !important;
          }

          #print-content .v-table__wrapper table {
            width: 100% !important;
          }

          #print-content .v-table__wrapper table td,
          #print-content .v-table__wrapper table th {
            white-space: normal !important;
            word-wrap: break-word !important;
          }

          @media print {
            @page {
              size: landscape;
            }
          }
        `,
        printType: 'Appointments List'
      });
    },
    /**
     * Handles printing of the provided source element.
     * @param {Object} params - The parameters for the print operation.
     * @param {string} params.sourceElementId - The ID of the source element to print.
     * @param {string} [params.containerToBeUsedAsPage] - The CSS selector for the container to use as a page. Not necessary if the `sourceElementId` is the container itself.
     * @param {string} [params.styles] - The custom styles to apply to the print.
     * @param {string} [params.printType] - The type of print to perform (for tracking purposes).
     */
    handlePrint({ sourceElementId, containerToBeUsedAsPage, styles, printType }) {
      let styleElement = null;
      let printContainer = null;

      const sourceElement = document.getElementById(sourceElementId);
      if (!sourceElement) {
        return;
      }

      const currentTimestamp = this.novaCore.formatDateTimeWithMilitarySupport(
        DateTime.now().toISO(),
        this.warehouse.timezone,
        this.novaCore.LuxonDateTimeFormats.MonthDayYearSlashedTimeAMPM,
        this.$isMilitaryTimeEnabled(this.warehouse),
        this.novaCore.LuxonDateTimeFormats.MonthDayYearSlashedTime24
      );

      const elementClone = sourceElement.cloneNode(true);
      const printContainerId = 'print-content';

      printContainer = document.getElementById(printContainerId);
      if (!printContainer) {
        printContainer = document.createElement('section');
        printContainer.id = printContainerId;
        document.body.appendChild(printContainer);
      }

      printContainer.innerHTML = '';

      const createHeader = () => {
        const header = document.createElement('header');
        header.className = 'printable-header';

        const logoContainer = document.createElement('div');
        logoContainer.className = 'logo-container';

        const logoImg = document.createElement('img');
        logoImg.src = '/images/logo_for_print.png';
        logoContainer.appendChild(logoImg);

        header.appendChild(logoContainer);
        header.appendChild(document.createTextNode(`Printed on ${currentTimestamp}`));
        return header;
      };

      const createFooter = () => {
        const footer = document.createElement('div');
        footer.className = 'printable-footer';
        footer.innerHTML = `
          <span class="bold" style="margin-right: 10px">Opendock</span>
          The ultimate dock scheduling software for warehouses.
        `;
        return footer;
      };

      // Add headers and footers to the appropriate elements
      const pageElements = containerToBeUsedAsPage
        ? elementClone.querySelectorAll(containerToBeUsedAsPage)
        : [elementClone];

      Array.from(pageElements).forEach(element => {
        element.insertBefore(createHeader(), element.firstChild);
        element.appendChild(createFooter());
      });

      printContainer.appendChild(elementClone);

      // Add custom styles if provided
      if (styles) {
        styleElement = document.createElement('style');
        styleElement.textContent = styles;
        document.head.appendChild(styleElement);
      }

      const logoImg = printContainer.querySelector('.printable-header img');

      /**
       * Wait for either the logo to load or fail before printing;
       * this ensure the logo has the chance to load before printing.
       */
      const loadLogoPromise = new Promise(resolve => {
        if (!logoImg) {
          // Logo image not found in header
          resolve();
          return;
        }

        const logoContainer = logoImg.parentElement;

        logoImg.onload = () => {
          resolve();
        };

        logoImg.onerror = () => {
          // If logo fails to load, we replace it with simple text
          logoContainer.innerHTML = '<span class="bold" style="margin-right: 10px">Opendock</span>';
          resolve();
        };
      });

      this.$nextTick(async () => {
        let printTimeout = null;
        let handleAfterPrint = null;
        let handleBeforeUnload = null;

        try {
          // Wait for logo to load or fail
          await loadLogoPromise;

          this.printStatus = 'printing';

          const printPromise = new Promise((resolve, reject) => {
            printTimeout = setTimeout(() => {
              reject(new Error('Print dialog timed out'));
            }, 2500);

            handleAfterPrint = () => {
              clearTimeout(printTimeout);
              resolve();
            };

            handleBeforeUnload = () => {
              clearTimeout(printTimeout);
              reject(new Error('Print dialog cancelled'));
            };

            window.addEventListener('afterprint', handleAfterPrint);
            window.addEventListener('beforeunload', handleBeforeUnload);

            /**
             * TODO: Observe if we''l need to check for print status
             * before proceeding with the print operation (or, for example,
             * if the use has navigate to another date while the print is
             * in progress).
             */
            window.print();
          });

          await printPromise;

          this.mixpanel.track(this.mixpanel.events.ACTION.PRINT_APPOINTMENTS, {
            'Org Name': this.$org.name,
            'Org ID': this.$org.id,
            'Warehouse Name': this.$selectedWarehouse?.name,
            'Warehouse ID': this.$selectedWarehouse?.id,
            'Print Type': printType,
            'Number of Appointments': this.listableAppointments?.length || 0,
            'View Type': this.viewType || 'N/A'
          });
        } catch (error) {
          /**
           * Print operation cancelled or failed.
           * Don't show error toast for cancellation as it's a user action
           */
          if (!error.message.includes('cancelled')) {
            this.notify('Failed to print. Please try again.', 'error');
          }
        } finally {
          if (handleAfterPrint) {
            window.removeEventListener('afterprint', handleAfterPrint);
          }

          if (handleBeforeUnload) {
            window.removeEventListener('beforeunload', handleBeforeUnload);
          }

          if (printTimeout) {
            clearTimeout(printTimeout);
          }

          if (styleElement) {
            document.head.removeChild(styleElement);
          }

          if (printContainer) {
            printContainer.innerHTML = '';
          }

          this.printStatus = 'idle';
        }
      });
    },
    async getCustomFormData(appointmentId, assetVisitId) {
      try {
        const searchCriteria = {
          s: {
            $or: [
              { objectId: appointmentId, entityName: 'appointment' },
              ...(assetVisitId ? [{ objectId: assetVisitId, entityName: 'assetvisit' }] : [])
            ]
          },
          limit: 1000
        };

        const response = await customFormsService.getCustomFormData(
          searchCriteria,
          {},
          {
            signal: this.abortController?.signal
          }
        );
        const customFormData = response || [];

        return customFormData;
      } catch (error) {
        console.error('Failed to fetch custom form data:', {
          appointmentId,
          assetVisitId,
          error: error.message
        });
        return [];
      }
    },
    async enrichAppointmentsWithCustomFormData(appointments) {
      if (!Array.isArray(appointments) || appointments.length === 0) {
        return [];
      }

      // Abort any pending requests from previous calls
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      try {
        const customFormDataPromises = appointments.map(appointment => {
          return this.getCustomFormData(appointment.id, appointment.assetVisit?.id);
        });

        const customFormDataResults = await Promise.allSettled(customFormDataPromises);

        return appointments.map((appointment, index) => ({
          ...appointment,
          customFormData:
            customFormDataResults[index].status === 'fulfilled'
              ? customFormDataResults[index].value
              : []
        }));
      } catch (error) {
        console.error('Failed to enrich appointments with custom form data:', error);
        return appointments;
      }
    },
    filterCustomFormDataByTriggerId(customFormData, triggerId) {
      return (customFormData || []).filter(item => item.triggerId === triggerId);
    }
  },
  async mounted() {
    await this.handleApptIdURLQuery();
    const storedSelectedColumns = this.storedSelectedColumns;
    this.selectedColumns =
      storedSelectedColumns.length > 0 ? storedSelectedColumns : this.selectColumns;
  },
  watch: {
    $selectedEvent() {
      this.selectedEvent = this.$selectedEvent;
    },
    $selectedDate() {
      if (this.abortController) {
        this.abortController.abort();
      }

      this.printStatus = 'idle';
    },
    'filters.searchStr'() {
      if (!this.filters.searchStr) {
        this.expanded = [];
      }
    },
    'filters.dateRange'() {
      this.$nextTick(() => {
        if (this.isRange && this.filters.dateRange.length === 1) {
          return;
        }
        this.options.page = 1;
      });
    },
    selectedColumns() {
      this.updateSelectedColumns();
      this.handleColumnSelect();
    },
    async $route(newVal, oldVal) {
      const shouldFetchEvent =
        newVal.query.appointmentId &&
        newVal.query.appointmentId !== oldVal.query.appointmentId &&
        this.$selectedEvent?.id !== newVal.query.appointmentId;
      if (shouldFetchEvent) {
        await this.fetchAndOpenAppt(newVal.query.appointmentId);
      }
    },
    beforeUnmount() {
      if (this.abortController) {
        this.abortController.abort();
      }
    }
  }
};
</script>
